'use client';

import { useState, useEffect } from 'react';
import { useLanguage } from '@/app/context/AppContext';
import { Translations, t as translateFunc } from '@/lib/i18n';

// ─── Static bundle of the default (EN) translations ───────────────────────────
// Imported statically so it is part of the JS bundle - zero async round-trip.
// This guarantees that t() returns a real string immediately on first render,
// eliminating the "admin.overview.title" flash that happens on slow networks.
import enTranslations from '@/lib/i18n/en.json';

/**
 * Hook to use translations in client components.
 * - Returns English translations instantly (no async, no flash).
 * - Overrides with the user-selected language asynchronously.
 */
export function useTranslation() {
  const { lang } = useLanguage();

  // Initialise with EN so we ALWAYS have real strings from frame 1.
  const [translations, setTranslations] = useState<Translations>(
    enTranslations as unknown as Translations,
  );
  const [loading, setLoading] = useState(lang !== 'en');

  useEffect(() => {
    // EN is already loaded - skip the async fetch.
    if (lang === 'en') {
      setTranslations(enTranslations as unknown as Translations);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    import(`@/lib/i18n/${lang}.json`)
      .then((mod) => {
        if (isMounted) {
          setTranslations((mod.default || mod) as Translations);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error(`Failed to load translations for ${lang}:`, err);
        // Fall back to PT - already loaded, so just stop the spinner.
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [lang]);

  const t = (key: string, fallback?: string): string => {
    const result = translateFunc(key, translations);
    // translateFunc returns the key itself when not found - use optional fallback.
    if (result === key && fallback !== undefined) return fallback;
    return result;
  };

  return { t, lang, loading, translations: translations as unknown as Record<string, unknown> };
}
